import { BetterAuthSessionSchema } from '@trellis/shared'
import type { BetterAuthSession, RequestResult } from '@trellis/shared'

import { authClient } from '@/lib/auth-client'
import { resolveTwoFactorAuthError } from '@/lib/two-factor'

const UNEXPECTED_SESSION_ERROR = {
  code: 'AUTH_ERROR',
  message: 'Unexpected session shape from auth provider',
} as const

interface SessionStoreValue {
  data: unknown
  error: { message?: string } | null
  isPending: boolean
  isRefetching: boolean
  refetch: () => Promise<void>
}

/**
 * Fetches the freshly-established session and writes it into the useSession store
 * synchronously. Shared by signIn() and the two-factor verification requests, all of
 * which complete authentication outside any mounted useSession() subscriber — a store
 * refetch() is deliberately not used: better-auth aborts in-flight store fetches from its
 * own lifecycle (signal refetches, delayed unmount cleanup), which would fail
 * intermittently right after these calls.
 */
async function resolveSessionAfterAuth(): Promise<RequestResult<BetterAuthSession>> {
  const sessionResult = await authClient.getSession()

  if (sessionResult.error) {
    return { status: 500, data: null, pagination: null, error: UNEXPECTED_SESSION_ERROR }
  }

  const parsed = BetterAuthSessionSchema.safeParse(sessionResult.data)

  if (!parsed.success) {
    return { status: 500, data: null, pagination: null, error: UNEXPECTED_SESSION_ERROR }
  }

  // Cast crosses better-auth's untyped $store.atoms boundary (Record<string, WritableAtom<any>>)
  const sessionStore = authClient.$store.atoms.session
  const sessionStoreValue = sessionStore.get() as SessionStoreValue
  sessionStore.set({
    ...sessionStoreValue,
    data: sessionResult.data,
    error: null,
    isPending: false,
  })

  return { status: 200, data: parsed.data, pagination: null, error: null }
}

/**
 * A successful sign-in on a 2FA-enrolled account never reaches the branches below —
 * better-auth deletes the partial session and returns this shape instead. The caller must
 * route to the 2FA verification step rather than treat it as a session.
 */
export type SignInResult =
  | { twoFactorRequired: true; twoFactorMethods: string[] }
  | RequestResult<BetterAuthSession>

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const signInResult = await authClient.signIn.email({ email, password })

  if (signInResult.error) {
    return {
      status: signInResult.error.status ?? 401,
      data: null,
      pagination: null,
      error: {
        code: 'AUTH_ERROR',
        message: signInResult.error.message ?? 'Sign in failed',
      },
    }
  }

  if (signInResult.data && 'twoFactorRedirect' in signInResult.data) {
    /*
     * better-auth's /sign-in/email after-hook injects { twoFactorRedirect, twoFactorMethods }
     * in place of a session when 2FA is required — a shape the base client type doesn't
     * declare, since it's added by the plugin's server-side hook rather than the endpoint's
     * own response type.
     */
    const twoFactorData = signInResult.data as {
      twoFactorRedirect: true
      twoFactorMethods?: string[]
    }
    return {
      twoFactorRequired: true,
      twoFactorMethods: twoFactorData.twoFactorMethods ?? [],
    }
  }

  return resolveSessionAfterAuth()
}

export async function verifyTwoFactorTotp(
  code: string,
  trustDevice: boolean,
): Promise<RequestResult<BetterAuthSession>> {
  const result = await authClient.twoFactor.verifyTotp({ code, trustDevice })

  if (result.error) {
    return {
      status: result.error.status ?? 401,
      data: null,
      pagination: null,
      error: resolveTwoFactorAuthError(result.error, 'Invalid verification code'),
    }
  }

  return resolveSessionAfterAuth()
}

export async function sendTwoFactorOtp(): Promise<RequestResult<null>> {
  const result = await authClient.twoFactor.sendOtp()
  if (result.error) {
    return {
      status: result.error.status ?? 400,
      data: null,
      pagination: null,
      error: resolveTwoFactorAuthError(result.error, 'Could not send verification code'),
    }
  }
  return { status: 200, data: null, pagination: null, error: null }
}

export async function verifyTwoFactorOtp(
  code: string,
  trustDevice: boolean,
): Promise<RequestResult<BetterAuthSession>> {
  const result = await authClient.twoFactor.verifyOtp({ code, trustDevice })

  if (result.error) {
    return {
      status: result.error.status ?? 401,
      data: null,
      pagination: null,
      error: resolveTwoFactorAuthError(result.error, 'Invalid verification code'),
    }
  }

  return resolveSessionAfterAuth()
}

export async function verifyTwoFactorBackupCode(
  code: string,
  trustDevice: boolean,
): Promise<RequestResult<BetterAuthSession>> {
  const result = await authClient.twoFactor.verifyBackupCode({ code, trustDevice })

  if (result.error) {
    return {
      status: result.error.status ?? 401,
      data: null,
      pagination: null,
      error: resolveTwoFactorAuthError(result.error, 'Invalid backup code'),
    }
  }

  return resolveSessionAfterAuth()
}

export async function signOut(): Promise<RequestResult<null>> {
  const result = await authClient.signOut()
  if (result.error) {
    return {
      status: result.error.status ?? 500,
      data: null,
      pagination: null,
      error: { code: 'AUTH_ERROR', message: result.error.message ?? 'Sign out failed' },
    }
  }
  return { status: 200, data: null, pagination: null, error: null }
}

export async function verifyEmail(token: string): Promise<RequestResult<null>> {
  const result = await authClient.verifyEmail({ query: { token } })
  if (result.error) {
    return {
      status: result.error.status ?? 400,
      data: null,
      pagination: null,
      error: {
        code: 'AUTH_ERROR',
        message: result.error.message ?? 'Email verification failed',
      },
    }
  }
  return { status: 200, data: null, pagination: null, error: null }
}

export async function forgotPassword(email: string): Promise<RequestResult<null>> {
  const result = await authClient.requestPasswordReset({
    email,
    redirectTo: '/reset-password',
  })
  if (result.error) {
    return {
      status: result.error.status ?? 400,
      data: null,
      pagination: null,
      error: { code: 'AUTH_ERROR', message: result.error.message ?? 'Request failed' },
    }
  }
  return { status: 200, data: null, pagination: null, error: null }
}

export async function resetPassword(
  token: string,
  password: string,
): Promise<RequestResult<null>> {
  const result = await authClient.resetPassword({ newPassword: password, token })
  if (result.error) {
    return {
      status: result.error.status ?? 400,
      data: null,
      pagination: null,
      error: {
        code: 'AUTH_ERROR',
        message: result.error.message ?? 'Password reset failed',
      },
    }
  }
  return { status: 200, data: null, pagination: null, error: null }
}

export async function updateProfileName(name: string): Promise<RequestResult<null>> {
  const result = await authClient.updateUser({ name })
  if (result.error) {
    return {
      status: result.error.status ?? 400,
      data: null,
      pagination: null,
      error: { code: 'AUTH_ERROR', message: result.error.message ?? 'Update failed' },
    }
  }
  return { status: 200, data: null, pagination: null, error: null }
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<RequestResult<null>> {
  const result = await authClient.changePassword({ currentPassword, newPassword })
  if (result.error) {
    return {
      status: result.error.status ?? 400,
      data: null,
      pagination: null,
      error: {
        code: 'AUTH_ERROR',
        message: result.error.message ?? 'Password change failed',
      },
    }
  }
  return { status: 200, data: null, pagination: null, error: null }
}

export interface EnableTwoFactorResponse {
  totpURI: string
  backupCodes: string[]
}

export async function enableTwoFactor(
  password: string,
): Promise<RequestResult<EnableTwoFactorResponse>> {
  const result = await authClient.twoFactor.enable({ password })
  if (result.error) {
    return {
      status: result.error.status ?? 400,
      data: null,
      pagination: null,
      error: { code: 'AUTH_ERROR', message: result.error.message ?? 'Enable failed' },
    }
  }
  return { status: 200, data: result.data, pagination: null, error: null }
}

/**
 * Confirms TOTP enrolment from an active session (SecurityTab / /2fa-setup) — the sibling
 * of {@link verifyTwoFactorTotp}, which instead completes a pending sign-in.
 * `twoFactorEnabled` only flips to true once the backend records this call as
 * successful.
 */
export async function confirmTwoFactorSetup(code: string): Promise<RequestResult<null>> {
  const result = await authClient.twoFactor.verifyTotp({ code })
  if (result.error) {
    return {
      status: result.error.status ?? 400,
      data: null,
      pagination: null,
      error: {
        code: 'AUTH_ERROR',
        message: result.error.message ?? 'Invalid verification code',
      },
    }
  }
  return { status: 200, data: null, pagination: null, error: null }
}

/**
 * Confirms email OTP enrolment from an active session (SecurityTab / /2fa-setup) — the
 * sibling of {@link verifyTwoFactorOtp}, which instead completes a pending sign-in. Unlike
 * TOTP, there is no separate password-gated "enable" step first: a code sent to the
 * user's own verified email is the enrolment proof, and `twoFactorEnabled` flips to true
 * on the first successful verification if it wasn't already.
 */
export async function confirmTwoFactorOtpSetup(
  code: string,
): Promise<RequestResult<null>> {
  const result = await authClient.twoFactor.verifyOtp({ code })
  if (result.error) {
    return {
      status: result.error.status ?? 400,
      data: null,
      pagination: null,
      error: {
        code: 'AUTH_ERROR',
        message: result.error.message ?? 'Invalid verification code',
      },
    }
  }
  return { status: 200, data: null, pagination: null, error: null }
}

export async function disableTwoFactor(password: string): Promise<RequestResult<null>> {
  const result = await authClient.twoFactor.disable({ password })
  if (result.error) {
    return {
      status: result.error.status ?? 400,
      data: null,
      pagination: null,
      error: { code: 'AUTH_ERROR', message: result.error.message ?? 'Disable failed' },
    }
  }
  return { status: 200, data: null, pagination: null, error: null }
}

export async function generateTwoFactorBackupCodes(
  password: string,
): Promise<RequestResult<string[]>> {
  const result = await authClient.twoFactor.generateBackupCodes({ password })
  if (result.error) {
    return {
      status: result.error.status ?? 400,
      data: null,
      pagination: null,
      error: {
        code: 'AUTH_ERROR',
        message: result.error.message ?? 'Could not regenerate backup codes',
      },
    }
  }
  return { status: 200, data: result.data.backupCodes, pagination: null, error: null }
}
